import React from "react"
import { Cell } from "fixed-data-table-2"
import { patchColumnData, editColumnData } from "sheets/sheet-action-creators"
import { UserSelect, ContactSelect } from "selects/containers/PieSelect"
import { connect } from "react-redux"
import { USER_RECIPIENT_KEY, SUPPORTER_RECIPIENT_KEY } from 'shared/imports/sharedConstants'
import { getDisplayName } from "shared/imports/sharedHelperFunctions"

const get_select_values = (multi, children, recipientKey) => {
    if (multi){
        return children && children.map(assignment =>
                // need to return undefined because default is null which is
                // an object (causes problems later on when the initial value)
                // is passed into the PieSelect
                assignment.get(`${recipientKey}__id`) || undefined
                ).toJS()
    }

    else {
        return (
            (children && !children.isEmpty())
            && children.get(0).get(`${recipientKey}__id`)
            || undefined
        )
    }
}

// need to pass in the recipientFirstName / LastName key because they're different
// for users and supporteres
const makeAssignmentCell = (recipientKey, recipientFirstNameKey, recipientLastNameKey, multi=true, isContact=false) =>
    ({ children, patchColumnData, editColumnData, sheetId, rowId, columnId, editing, ...props }) => {
        const Component  = isContact ? ContactSelect : UserSelect
        Component.displayName = getDisplayName(isContact ? ContactSelect : UserSelect)
        const AssignmentCell = (
            <div className="user-select-cell">
                <Cell {...props}>
                    {
                    // if we're currently editing the contents of this cell
                    editing
                    // display a select of users
                    ?   <div
                            // neccessary because the Select onClick() event propagates down to the SheetCell onClick() every time we click on a select,
                            // which re-calls clickCell() after the unclickCell() action creator is fired in patchColumnData of sheet-action-creators
                            // Since it is not possible to pass an onClick prop down to a react-select component, this is necessary
                            // https://github.com/JedWatson/react-select/issues/532
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                            }}
                        >
                            <Component
                                // populated with the set of assigned users
                                value={
                                    get_select_values(multi, children, recipientKey)
                                }
                                autoload={false || editing}
                                onChange={(selections) => {
                                    if (!multi) {
                                        if (!Array.isArray(selections)) {
                                            selections = [selections]
                                        }
                                    }

                                    // add it to the store, making it look like an assignment,
                                    // and send it to the backend as just an array of ids
                                    // to create assignments.
                                    patchColumnData({
                                        id: sheetId,
                                        columnId,
                                        rowId,
                                        newValue: selections.map(({ id }) => id),
                                        toStoreValue: selections.map(selection => ({
                                            [`${recipientKey}__id`]: selection.id,
                                            [`${recipientKey}__${recipientFirstNameKey}`]: selection[recipientFirstNameKey],
                                            [`${recipientKey}__${recipientLastNameKey}`]: selection[recipientLastNameKey],
                                        }))
                                    })
                                }}
                                multi={multi}
                            />
                        </div>
                    // if we aren't editing this cell
                    : (
                        children &&
                        // display a comma separated string of the names of
                        // all the users that are assigned to this row
                        children.map(assignment => [
                                assignment.get(`${recipientKey}__${recipientFirstNameKey}`),
                                assignment.get(`${recipientKey}__${recipientLastNameKey}`)
                            ].join(" ").trim()
                        ).filter(value => !!value).join(", ")
                    )
                }
                </Cell>
            </div>
        )
        return AssignmentCell
    }

const connectAssignment = AssignmentCell => connect(null, { patchColumnData, editColumnData })(AssignmentCell)

export const UserAssignmentCell = connectAssignment(makeAssignmentCell(
    USER_RECIPIENT_KEY,
    'first_name',
    'last_name',
))

export const UserAssignmentSingleCell = connectAssignment(makeAssignmentCell(
    USER_RECIPIENT_KEY,
    'first_name',
    'last_name',
    false,
))

export const ContactAssignmentCell = connectAssignment(makeAssignmentCell(
    SUPPORTER_RECIPIENT_KEY,
    'firstname',
    'lastname',
    true,
    true,
))
